/* Collapse component
 - Usage:
   <button class="collapse__trigger" data-collapse-target="#myContent" aria-expanded="false">Toggle</button>
   <div id="myContent" class="collapse__content" data-collapse-content aria-hidden="true">...</div>

 - Optional: data-collapse-group="group-name" on trigger to make an accordion (only one open in group)
 - Fires custom events on elements: collapse:show, collapse:shown, collapse:hide, collapse:hidden, collapse:toggle
*/
(function () {
  'use strict';

  var SELECTOR_TRIGGER = '[data-collapse-target]';
  var CLASS_EXPANDED = 'is-expanded';
  var CLASS_COLLAPSING = 'is-collapsing';
  var DEFAULT_DURATION = 200; // fallback if CSS var not available

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function dispatchEvent(el, name, detail) {
    var event = new CustomEvent(name, { bubbles: true, cancelable: true, detail: detail });
    return el.dispatchEvent(event);
  }

  function getDurationFromCSS() {
    var root = getComputedStyle(document.documentElement);
    var val = root.getPropertyValue('--duration-default').trim();
    if (!val) return DEFAULT_DURATION;
    // naive parse: supports 200ms or 0.2s
    if (val.endsWith('ms')) return parseFloat(val);
    if (val.endsWith('s')) return parseFloat(val) * 1000;
    return DEFAULT_DURATION;
  }

  function getTarget(trigger) {
    var selector = trigger.getAttribute('data-collapse-target') || trigger.getAttribute('aria-controls');
    if (!selector) return null;
    try {
      return document.querySelector(selector);
    } catch (e) {
      // maybe the selector is an id without #
      return document.getElementById(selector.replace('#', ''));
    }
  }

  function show(trigger, content) {
    if (!dispatchEvent(content, 'collapse:show', { trigger: trigger, content: content })) return; // cancelled

    // set states
    trigger.setAttribute('aria-expanded', 'true');
    content.setAttribute('aria-hidden', 'false');

    if (prefersReducedMotion()) {
      content.classList.add(CLASS_EXPANDED);
      dispatchEvent(content, 'collapse:shown', { trigger: trigger, content: content });
      return;
    }

    var duration = getDurationFromCSS();

    // set a lock to prevent immediate re-toggle during/shortly after animation
    content._collapseLockUntil = Date.now() + duration + 100;
    if ((trigger.dataset && trigger.dataset.collapseDebug === 'true') || window.CollapseDebug) {
      console.log('[collapse] show start', { id: content.id, duration: duration });
    }

    // cancel existing animation
    stopAnimation(content);

    content.classList.add(CLASS_COLLAPSING);
    // ensure visible
    content.style.display = content.style.display === 'none' ? '' : content.style.display;

    // start from height 0 (or current) to scrollHeight
    var startHeight = content.getBoundingClientRect().height;
    // Temporarily set height to auto to measure scrollHeight
    content.style.height = 'auto';
    var targetHeight = content.scrollHeight;
    content.style.height = startHeight + 'px';

    // force reflow
    // eslint-disable-next-line no-unused-expressions
    content.offsetHeight;

    requestAnimationFrame(function () {
      content.style.transition = 'height ' + duration + 'ms ease';
      content.style.height = targetHeight + 'px';

      var called = false;
      var onEnd = function (e) {
        if (e && e.target !== content) return;
        if (called) return;
        called = true;
        content.removeEventListener('transitionend', onEnd);

        // cleanup reference
        try { delete content._collapseOnEnd; } catch (x) { content._collapseOnEnd = null; }

        content.style.transition = '';
        content.style.height = '';
        content.classList.remove(CLASS_COLLAPSING);
        content.classList.add(CLASS_EXPANDED);

        // extend lock a little after finish to avoid immediate toggles
        content._collapseLockUntil = Date.now() + 100;

        if ((trigger.dataset && trigger.dataset.collapseDebug === 'true') || window.CollapseDebug) {
          console.log('[collapse] show end', { id: content.id });
        }

        dispatchEvent(content, 'collapse:shown', { trigger: trigger, content: content });
      };

      // store handler reference so stopAnimation can remove it
      content._collapseOnEnd = onEnd;
      content.addEventListener('transitionend', onEnd);

      // Fallback: if transitionend not fired
      content._collapseTimeout = setTimeout(function () {
        // call stored handler (if still present)
        try { onEnd(); } catch (e) {}
      }, duration + 50);
    });
  }

  function hide(trigger, content) {
    if (!dispatchEvent(content, 'collapse:hide', { trigger: trigger, content: content })) return; // cancelled

    trigger.setAttribute('aria-expanded', 'false');
    content.setAttribute('aria-hidden', 'true');

    if (prefersReducedMotion()) {
      content.classList.remove(CLASS_EXPANDED);
      dispatchEvent(content, 'collapse:hidden', { trigger: trigger, content: content });
      return;
    }

    var duration = getDurationFromCSS();

    // set a lock to prevent immediate re-toggle during/shortly after animation
    content._collapseLockUntil = Date.now() + duration + 100;
    if ((trigger.dataset && trigger.dataset.collapseDebug === 'true') || window.CollapseDebug) {
      console.log('[collapse] hide start', { id: content.id, duration: duration });
    }

    // cancel existing animation
    stopAnimation(content);

    var startHeight = content.getBoundingClientRect().height;
    content.style.height = startHeight + 'px';

    // force reflow
    // eslint-disable-next-line no-unused-expressions
    content.offsetHeight;

    content.classList.add(CLASS_COLLAPSING);
    requestAnimationFrame(function () {
      content.style.transition = 'height ' + duration + 'ms ease';
      content.style.height = '0px';

      var called = false;
      var onEnd = function (e) {
        if (e && e.target !== content) return;
        if (called) return;
        called = true;
        content.removeEventListener('transitionend', onEnd);

        // cleanup reference
        try { delete content._collapseOnEnd; } catch (x) { content._collapseOnEnd = null; }

        content.style.transition = '';
        content.style.height = '';
        content.classList.remove(CLASS_COLLAPSING);
        content.classList.remove(CLASS_EXPANDED);
        // optionally hide with display none
        // content.style.display = 'none';

        if ((trigger.dataset && trigger.dataset.collapseDebug === 'true') || window.CollapseDebug) {
          console.log('[collapse] hide end', { id: content.id });
        }

        dispatchEvent(content, 'collapse:hidden', { trigger: trigger, content: content });
      };

      content._collapseOnEnd = onEnd;
      content.addEventListener('transitionend', onEnd);

      content._collapseTimeout = setTimeout(function () {
        try { onEnd(); } catch (e) {}
      }, duration + 50);
    });
  }

  function toggle(trigger) {
    var content = getTarget(trigger);
    if (!content) return;
    // If locked (recently animating), ignore toggles
    if (content._collapseLockUntil && Date.now() < content._collapseLockUntil) return;
    // If currently animating, ignore additional toggles to avoid flicker/race conditions
    if (content.classList && content.classList.contains(CLASS_COLLAPSING)) return;
    var expanded = trigger.getAttribute('aria-expanded') === 'true';
    dispatchEvent(content, 'collapse:toggle', { trigger: trigger, content: content });

    if (expanded) {
      hide(trigger, content);
    } else {
      // If part of a group, close siblings
      var group = trigger.getAttribute('data-collapse-group');
      if (group) {
        closeGroupExcept(group, content);
      }
      show(trigger, content);
    }
  }

  function closeGroupExcept(groupName, exceptContent) {
    var triggers = document.querySelectorAll('[data-collapse-group="' + groupName + '"]');
    Array.prototype.forEach.call(triggers, function (tr) {
      var cont = getTarget(tr);
      if (!cont) return;
      if (cont === exceptContent) return;
      // only hide if currently expanded
      if (tr.getAttribute('aria-expanded') === 'true') {
        hide(tr, cont);
      }
    });
  }

  function stopAnimation(content) {
    // remove any pending transitionend listener
    if (content && content._collapseOnEnd) {
      try {
        content.removeEventListener('transitionend', content._collapseOnEnd);
      } catch (e) {}
      try { delete content._collapseOnEnd; } catch (x) { content._collapseOnEnd = null; }
    }
    // clear any fallback timeout
    if (content && content._collapseTimeout) {
      try { clearTimeout(content._collapseTimeout); } catch (e) {}
      try { delete content._collapseTimeout; } catch (x) { content._collapseTimeout = null; }
    }

    content.style.transition = '';
    content.classList.remove(CLASS_COLLAPSING);
  }

  function onClick(e) {
    var trigger = e.currentTarget;
    toggle(trigger);
  }

  function onKeydown(e) {
    var trigger = e.currentTarget;
    var key = e.key;
    if (key === ' ' || key === 'Enter') {
      e.preventDefault();
      toggle(trigger);
    }
  }

  function initTrigger(trigger) {
    // Prevent double initialization
    if (trigger.dataset && trigger.dataset.collapseInit === 'true') return;
    if (trigger.dataset) trigger.dataset.collapseInit = 'true';

    var content = getTarget(trigger);
    if (!content) return;

    // Ensure ARIA
    var expanded = trigger.getAttribute('aria-expanded');
    if (expanded === null) trigger.setAttribute('aria-expanded', 'false');
    if (!content.hasAttribute('aria-hidden')) content.setAttribute('aria-hidden', 'true');

    // initial classes
    if (trigger.getAttribute('aria-expanded') === 'true') {
      content.classList.add(CLASS_EXPANDED);
    }

    trigger.addEventListener('click', onClick);
    trigger.addEventListener('keydown', onKeydown);
  }

  function initAll(context) {
    context = context || document;
    var triggers = context.querySelectorAll(SELECTOR_TRIGGER);
    Array.prototype.forEach.call(triggers, function (tr) {
      initTrigger(tr);
    });
  }

  // Auto-init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Expose API
  window.Collapse = {
    init: initAll,
    toggle: function (selector) {
      var el = document.querySelector(selector);
      if (!el) return;
      var trigger = el.matches(SELECTOR_TRIGGER) ? el : el.querySelector(SELECTOR_TRIGGER);
      if (trigger) toggle(trigger);
    }
  };
})();
